/**
 * Every account, searchable.
 *
 * THE FILTERS ARE SERVER-SIDE AND THE PAGINATION IS A CURSOR, and those two
 * facts are the same fact: the console holds ONE page at a time and must never
 * imply otherwise. So there is no client-side sort control here — sorting the
 * rows in hand would order one page and present it as the ordering of the whole
 * set, which is how an operator concludes there are no verified users because
 * none appear on page one.
 *
 * `role: null` on a row means ORDINARY CUSTOMER — a meaningful value, not a
 * missing one — so it is rendered as a word ("customer"), never as a blank cell.
 * The filter offers `customer` as its own option for the same reason; a staff
 * console whose role filter can only narrow to staff cannot answer "show me the
 * customers", which is most of the table.
 *
 * Changing any filter RESETS the cursor. A cursor is a position in one ordering;
 * carrying it across a filter change asks the server to resume a list that no
 * longer exists.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminUsers } from '@/hooks/useAdmin';
import type { AdminUsersQuery } from '@/api/adminEndpoints';
import { Table, Thead, Tbody, Tr, Th, Td, TableEmpty } from '@/components/ui/Table';
import { Pill } from '@/components/ui/StatusPill';
import { Button } from '@/components/ui/Button';
import { formatCents } from '@/utils/money';
import { AdminLoading, AdminError, AdminPageHeader } from './AdminAsync';
import { PAGE_SIZE, applyFilter, applySearch, countActiveFilters } from './logic';

const ROLES: { value: NonNullable<AdminUsersQuery['role']>; label: string }[] = [
  { value: 'customer', label: 'Customers' },
  { value: 'owner', label: 'Owner' },
  { value: 'admin', label: 'Admin' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'support', label: 'Support' },
  { value: 'readonly', label: 'Read-only' },
];

const KYC: NonNullable<AdminUsersQuery['kycStatus']>[] = [
  'unverified',
  'pending',
  'verified',
  'rejected',
];

const STATUSES: NonNullable<AdminUsersQuery['status']>[] = ['active', 'suspended', 'closed'];

/** Muted for every KYC state but `rejected` — see the note in StatusPill. */
function kycTone(status: string) {
  if (status === 'verified') return 'positive' as const;
  if (status === 'rejected') return 'caution' as const;
  return 'muted' as const;
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-lg border px-2.5 py-1 text-xs font-medium capitalize transition ${
        active
          ? 'border-brand-teal/35 bg-brand-teal/10 text-brand-teal'
          : 'border-white/10 bg-white/[0.03] text-white/50 hover:bg-white/[0.06] hover:text-white/80'
      }`}
    >
      {children}
    </button>
  );
}

export function UsersPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<AdminUsersQuery>({ limit: PAGE_SIZE });
  const [search, setSearch] = useState('');
  /** Cursors already visited, so "Back" is a pop rather than a re-query from 0. */
  const [trail, setTrail] = useState<string[]>([]);

  const query = useAdminUsers(filters);

  /** Any filter change invalidates the cursor — it indexes the previous ordering. */
  function setFilter<K extends keyof AdminUsersQuery>(key: K, value: AdminUsersQuery[K]) {
    setTrail([]);
    setFilters((prev) => applyFilter(prev, key, value));
  }

  function onSearch(e: React.FormEvent) {
    e.preventDefault();
    setTrail([]);
    setFilters((prev) => applySearch(prev, search));
  }

  const activeFilterCount = countActiveFilters(filters);

  return (
    <>
      <AdminPageHeader
        title="Users"
        subtitle="Search matches email, referral code or account id. Balances are point-in-time reads, not a ledger."
        actions={
          activeFilterCount > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearch('');
                setTrail([]);
                setFilters({ limit: PAGE_SIZE });
              }}
            >
              Clear {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'}
            </Button>
          ) : undefined
        }
      />

      <div className="mb-4 space-y-3">
        <form onSubmit={onSearch} className="flex gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Email, referral code or id"
            className="min-w-0 flex-1 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white/85 placeholder:text-white/25 focus:border-brand-teal/40 focus:outline-none"
          />
          <Button type="submit" variant="secondary" size="sm">
            Search
          </Button>
        </form>

        <div className="flex flex-wrap items-center gap-1.5">
          {ROLES.map((r) => (
            <Chip key={r.value} active={filters.role === r.value} onClick={() => setFilter('role', r.value)}>
              {r.label}
            </Chip>
          ))}
          <span className="mx-1 h-4 w-px bg-white/10" />
          {KYC.map((k) => (
            <Chip key={k} active={filters.kycStatus === k} onClick={() => setFilter('kycStatus', k)}>
              {k}
            </Chip>
          ))}
          <span className="mx-1 h-4 w-px bg-white/10" />
          {STATUSES.map((s) => (
            <Chip key={s} active={filters.status === s} onClick={() => setFilter('status', s)}>
              {s}
            </Chip>
          ))}
        </div>
      </div>

      {query.isLoading ? (
        <AdminLoading label="Reading accounts" />
      ) : query.isError || !query.data ? (
        <AdminError error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <>
          <Table>
            <Thead>
              <Tr>
                <Th>Account</Th>
                <Th>Role</Th>
                <Th>KYC</Th>
                <Th>Status</Th>
                <Th>Savings</Th>
                <Th>Balance</Th>
                <Th>Deposited</Th>
              </Tr>
            </Thead>
            <Tbody>
              {query.data.items.length === 0 ? (
                <TableEmpty colSpan={7}>
                  No account matches these filters. That is an answer, not an error — the read
                  succeeded.
                </TableEmpty>
              ) : (
                query.data.items.map((u) => (
                  <Tr key={u.id} onClick={() => navigate(`/admin/users/${u.id}`)}>
                    <Td>
                      <span className="block text-white/85">{u.email}</span>
                      <span className="mt-0.5 block text-[11px] text-white/35">
                        {u.firstName}
                        {u.referralCode ? ` · ${u.referralCode}` : ''}
                      </span>
                    </Td>
                    <Td>
                      {/* null is "ordinary customer" — a value, never a blank. */}
                      <Pill tone={u.role ? 'caution' : 'muted'}>{u.role ?? 'customer'}</Pill>
                    </Td>
                    <Td>
                      <Pill tone={kycTone(u.kycStatus)}>{u.kycStatus}</Pill>
                    </Td>
                    <Td>
                      <Pill tone={u.status === 'active' ? 'positive' : 'caution'}>{u.status}</Pill>
                    </Td>
                    <Td num>{formatCents(u.savingsCents)}</Td>
                    <Td num>{formatCents(u.balanceCents)}</Td>
                    <Td num>{formatCents(u.cumulativeDepositsCents)}</Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>

          <div className="mt-4 flex items-center justify-between gap-3">
            <span className="text-xs text-white/35">
              {query.data.items.length} account{query.data.items.length === 1 ? '' : 's'} on this
              page
              {query.data.nextCursor ? ' · more to follow' : ''}
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={trail.length === 0}
                onClick={() => {
                  const next = [...trail];
                  const prev = next.pop();
                  setTrail(next);
                  setFilters((f) => {
                    const updated = { ...f };
                    if (prev) updated.cursor = prev;
                    else delete updated.cursor;
                    return updated;
                  });
                }}
              >
                Back
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!query.data.nextCursor}
                onClick={() => {
                  const cursor = query.data?.nextCursor;
                  if (!cursor) return;
                  setTrail((t) => [...t, filters.cursor ?? '']);
                  setFilters((f) => ({ ...f, cursor }));
                }}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
